import React, { useContext, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import _ from "lodash"

import ROUTES from "../../../components/Routes/routesContants"
import { LoaderSmall } from "../../../components/Loaders"
// Prime React
import { SplitButton } from "primereact/splitbutton"
import useLabels from "../../../hooks/useLabels"

// Context
import { UserContext } from "../../../context/userContext"
import { ADVANCED_SEARCH } from "../queries"

// Reusable Components
import GenericTable from "../../../components/DataTable/GenericTable"
import withDataFetching from "../../../components/DataTable/withDataFetching"
import styles from "../../../styles/AdvanceSearchResult.module.css"

const AdvancedSearchResults = ({ filteredResults = null, fetching, data }) => {
  const navigate = useNavigate()
  const userCtx = useContext(UserContext)

  const participants = data?.search || []

  const activityIds = _.compact(
    participants.map((p) => p.id_participant_last_activity)
  )

  const [loadingLabels, labels] = useLabels(activityIds)

  if (fetching || loadingLabels) return <LoaderSmall />

  const results = filteredResults || participants

  if (!results) return null

  const handleResults = _.map(results, (participant) => {
    let items = []

    const posibleMembershipStatus = ["Active Unengaged", "Active Engaged"]

    if (
      _.includes(
        posibleMembershipStatus,
        participant.participant_membership_status
      ) ||
      participant.participant_membership_status === "Kiosk Member"
    ) {
      items = [
        {
          label: "Manage Services",
          command: async () => {
            await userCtx.changeParticipant(participant.participant_id)
            navigate("/participant/manage/service-episodes")
          },
        },
      ]
    } else {
      items = [
        {
          label: "Start New Service Episode",
          command: async () => {
            await userCtx.changeParticipant(participant.participant_id)
            navigate("/participant/manage/service-episodes")
          },
        },
      ]
    }

    let cleanLocations = _.trim(participant.locations).split(",")

    for (let x = 0; x < cleanLocations.length; x++) {
      cleanLocations[x] = _.trim(cleanLocations[x])
    }

    return {
      full_name: `${_.unescape(
        participant.participant_name_first
      )} ${_.unescape(participant.participant_name_last)}`,
      participant_id: participant.participant_alias,
      phone: participant.participant_phone_primary,
      email: participant.participant_email_account,
      status: participant.participant_membership_status,
      last_activity:
        labels[participant.id_participant_last_activity] ||
        participant.participant_last_activity,
      last_engagement: participant.participant_last_engagement,
      current_rsi: participant.current_rsi,
      programs: _.join(_.uniq(participant.program_list, ", ")),
      staff_full_name: _.unescape(participant.staff_full_name),
      locations: _.join(_.uniq(cleanLocations), ", "),
      link: (
        <SplitButton
          label="View"
          onClick={async () => {
            await userCtx.changeParticipant(participant.participant_id)
            navigate(ROUTES.PARTICIPANT_PROFILE)
          }}
          model={items}
          className={styles["split-button"]}
        />
      ),
    }
  })

  // RSI hidden by the moment.
  const columns = [
    { field: "full_name", header: "Name", sortable: true },
    { field: "locations", header: "Location", sortable: true },
    { field: "programs", header: "Program ID", sortable: true },
    { field: "staff_full_name", header: "Assigned Staff", sortable: true },
    { field: "status", header: "Status", sortable: true },
    { field: "last_activity", header: "Last Activity", sortable: true },
    { field: "last_engagement", header: "Last Engagement", sortable: true },
    // { field: "current_rsi", header: "Current RSI" },
    { field: "link", header: "Actions", sortable: true },
  ]

  return (
    <GenericTable
      data={handleResults}
      columns={columns}
      paginator={true}
      sortField="full_name"
      sortOrder={1}
      rows={50}
    />
  )
}

const variables = (userCtx, searchType) => ({
  staff_id: userCtx.session.user.staff_id,
  organization_id: userCtx.session.user.organization_id,
  search_type: searchType,
})

const WrappedAdvancedSearchResults = (props) => {
  const userCtx = useContext(UserContext)
  const searchVariables = variables(userCtx, props.searchType)

  return withDataFetching(
    AdvancedSearchResults,
    ADVANCED_SEARCH
  )({
    variables: searchVariables,
    ...props,
  })
}

export default WrappedAdvancedSearchResults
